import { Service, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxOption, OnyxOptional, OnyxOptionsGroup } from '@onyx/angular';
import { OnyxDatePipe, OnyxNamePipe } from '@onyx/angular';
import { chain, groupBy, intersection, orderBy, uniq } from 'lodash';
import { DateTime } from 'luxon';
import { DelegationHelper } from '../../dashboard/delegations/common/helpers/delegation.helper';
import { DriverHelper } from '../../dashboard/drivers/common/helpers/driver.helper';
import type { Driver } from '../../dashboard/drivers/common/interfaces/driver';
import { FleetCategory } from '../../dashboard/fleet/common/enums/fleet-category';
import type {
  Fleet,
  SimplifiedFleet,
} from '../../dashboard/fleet/common/interfaces/fleet';
import { DictionaryCode } from '../enums/dictionary-code';
import { ValidationSeverity } from '../enums/validation-severity';
import type { TelematicsDriver } from '../interfaces/telematics/telematics-driver';
import type { TelematicsItem } from '../interfaces/telematics/telematics-item';
import type { ValidationIssue } from '../interfaces/validation/validation-issue';
import { JoinPipe } from '../pipes/join.pipe';
import type { CellColor } from './color.helper';
import { ColorHelper } from './color.helper';

export enum ValidationContext {
  FLEET = 'fleet',
  SET = 'set',
  DELEGATION = 'delegation',
  DRIVER = 'driver',
  ORDER = 'order',
  SWAP_PANEL = 'swap',
}

@Service()
export class ValidationConditionsHelper {
  private readonly translateService = inject(TranslateService);
  private readonly joinPipe = inject(JoinPipe);
  private readonly datePipe = inject(OnyxDatePipe);
  private readonly namePipe = inject(OnyxNamePipe);

  private readonly I18N = 'issues';

  public checkCountries(
    context: ValidationContext,
    issues: ValidationIssue[],
    data?: {
      driver?: Driver;
      secondaryDriver?: Driver;
      vehicle?: Fleet;
      trailer?: Fleet;
    },
  ): void {
    const { driver, secondaryDriver, vehicle, trailer } = data ?? {};
    if (!vehicle || !driver) return;

    const countries = uniq([
      ...(vehicle.allowedCountries.whitelist ?? []),
      ...(trailer?.allowedCountries.whitelist ?? []),
    ]);

    const driverCountries = uniq([
      ...(driver?.countries.whitelist ?? []),
      ...(secondaryDriver?.countries.whitelist ?? []),
    ]);

    if (intersection(driverCountries, countries).length === 0) {
      issues.push({
        message: `${this.I18N}.driverAndVehicleDifferentCountries`,
        severity: {
          [ValidationContext.DELEGATION]: ValidationSeverity.ERROR,
          [ValidationContext.DRIVER]: ValidationSeverity.WARNING,
          [ValidationContext.FLEET]: ValidationSeverity.WARNING,
          [ValidationContext.SET]: ValidationSeverity.WARNING,
          [ValidationContext.ORDER]: ValidationSeverity.WARNING,
          [ValidationContext.SWAP_PANEL]: ValidationSeverity.WARNING,
        }[context],
      });
    }
  }

  public checkSkills(
    driver: Driver,
    issues: ValidationIssue[],
    vehicle?: Fleet,
    trailer?: Fleet,
  ): void {
    const vehicleTrailer = trailer ?? vehicle?.trailer;
    if (!vehicleTrailer) return;

    const requiredSkill = vehicleTrailer.generalInformation.type;
    const hasSkill = driver.skills.semiTrailers?.includes(requiredSkill ?? '');
    if (hasSkill) return;

    issues.push({
      message: `${this.I18N}.driverMissingSkill`,
      data: {
        skill: this.translateService.instant(
          `${DictionaryCode.VEHICLE_TYPE}.${requiredSkill}`,
        ),
      },
      severity: ValidationSeverity.WARNING,
    });
  }

  public checkAdr(
    driver: Driver,
    issues: ValidationIssue[],
    vehicle?: Fleet,
    trailer?: Fleet,
  ): void {
    if (!vehicle) return;

    const vehicleTrailer = trailer ?? vehicle.trailer;
    const fleetAdrClasses = uniq([
      ...(vehicle.additionalParameters.adrClasses ?? []),
      ...(vehicleTrailer?.additionalParameters.adrClasses ?? []),
    ]);

    if (
      fleetAdrClasses.length &&
      !intersection(
        DriverHelper.getDriverAdrClasses(
          driver,
          vehicle.generalInformation.type,
        ),
        fleetAdrClasses,
      ).length
    ) {
      issues.push({
        message: `${this.I18N}.noAdrClasses`,
        severity: ValidationSeverity.WARNING,
      });
    }
  }

  public checkRequiredDrivingLicense(
    context: ValidationContext,
    driver: Driver | TelematicsDriver,
    issues: ValidationIssue[],
    options?: {
      vehicle?: Fleet | TelematicsItem | null;
      trailer?: Fleet | SimplifiedFleet | TelematicsItem | null;
      showDriverName?: boolean;
    },
  ): void {
    const { vehicle, trailer, showDriverName } = options ?? {};
    const categories =
      driver.driversLicenseAndProfessionalQualifications.categories.map(
        ({ category }) => category,
      );

    const checkRequiredCategories = (requiredCategories: string[]) => {
      if (
        !requiredCategories.some((category) => categories.includes(category))
      ) {
        issues.push({
          message: `${this.I18N}.required${showDriverName ? 'Driver' : ''}DrivingLicense`,
          data: {
            category: requiredCategories
              .map((key) =>
                this.translateService.instant(
                  `${DictionaryCode.DRIVERS_LICENSE_CATEGORY}.${key}`,
                ),
              )
              .join(` ${this.translateService.instant('labels.or')} `),
            ...(showDriverName && {
              driver: this.namePipe.transform(driver.driverData),
            }),
          },
          severity:
            context === ValidationContext.SWAP_PANEL
              ? ValidationSeverity.WARNING
              : ValidationSeverity.ERROR,
        });
      }
    };

    const category = (vehicle ?? trailer)?.generalInformation.category;
    if (!category) return;

    const requiredCategoriesData = this.getRequiredCategoryMap()[category];
    if (requiredCategoriesData) {
      const hasTrailer = this.hasVehicleTrailer(vehicle, trailer, context);
      const requiredCategories = hasTrailer
        ? requiredCategoriesData.withTrailer
        : requiredCategoriesData.withoutTrailer;

      checkRequiredCategories(requiredCategories);
    }
  }

  public checkActiveDelegation(
    item: Fleet | Driver,
    issues: ValidationIssue[],
  ): void {
    const delegation = item.delegation;
    if (!delegation || !DelegationHelper.isActive(delegation)) return;

    const isFleet = 'generalInformation' in item;
    issues.push({
      message: isFleet
        ? 'labels.vehicleHasActiveDelegation'
        : 'labels.driverHasActiveDelegation',
      severity: ValidationSeverity.ERROR,
    });
  }

  public checkDrivingLicenseExpiryDate(
    context: ValidationContext,
    driver: Driver | TelematicsDriver,
    issues: ValidationIssue[],
    options?: {
      vehicle?: Fleet | TelematicsItem | null;
      trailer?: Fleet | SimplifiedFleet | TelematicsItem | null;
    },
  ): void {
    const { vehicle, trailer } = options ?? {};
    const drivingStatuses = chain(
      driver.driversLicenseAndProfessionalQualifications.categories,
    )
      .filter(({ expirationDate }) => expirationDate?.date != null)
      .map(({ category, expirationDate }) => ({
        category,
        expirationDate: expirationDate.date,
      }))
      .thru((categories) =>
        this.getExpiryStatuses(
          categories,
          (category) => category.expirationDate,
          ColorHelper.getCellColor,
        ),
      )
      .value();
    if (!drivingStatuses) return;

    const filteredDrivingStatuses = () => {
      const translateCategory = (category: string) =>
        this.translateService.instant(
          `${DictionaryCode.DRIVERS_LICENSE_CATEGORY}.${category}`,
        );

      const mapCategories = (
        statuses:
          typeof drivingStatuses.errors | typeof drivingStatuses.warnings,
      ) =>
        statuses.map((status) => ({
          ...status,
          category: translateCategory(status.category),
        }));

      const category = (vehicle ?? trailer)?.generalInformation.category;
      if (!category) {
        return {
          errors: mapCategories(drivingStatuses.errors),
          warnings: mapCategories(drivingStatuses.warnings),
        };
      }

      const hasTrailer = this.hasVehicleTrailer(vehicle, trailer, context);
      const requiredCategories =
        this.getRequiredCategoryMap()[category]?.[
          hasTrailer ? 'withTrailer' : 'withoutTrailer'
        ] ?? [];

      const errors: typeof drivingStatuses.errors = [];
      const warnings = drivingStatuses.warnings;

      const invalidRequired = requiredCategories.some((category) =>
        drivingStatuses.errors.find((status) => status.category === category),
      );
      for (const status of drivingStatuses.errors) {
        if (requiredCategories.includes(status.category) && invalidRequired) {
          errors.push({ ...status, color: 'red' });
        } else {
          warnings.push({ ...status, color: 'yellow' });
        }
      }

      if (context === ValidationContext.SWAP_PANEL) {
        return {
          errors: [],
          warnings: mapCategories([
            ...drivingStatuses.errors,
            ...drivingStatuses.warnings,
          ]),
        };
      }

      return {
        errors: mapCategories(errors),
        warnings: mapCategories(drivingStatuses.warnings),
      };
    };

    const handleDrivingLicenseExpiry = (
      items: typeof drivingStatuses.errors | typeof drivingStatuses.warnings,
      single: string,
      multiple: string,
      showDate = false,
    ) =>
      this.addExpiryIssue(
        items,
        `${this.I18N}.drivingLicense.${single}`,
        `${this.I18N}.drivingLicense.${multiple}`,
        issues,

        {
          getData: (item) => ({
            licenses: item.category,
            ...(showDate && {
              date: this.datePipe.transform(item.expirationDate!, 'date-dot'),
            }),
          }),
          getListData: (items) => ({
            licenses: this.joinPipe.transform(
              items.map((item) => item.category),
              { maxLength: 5 },
            ),
            date: this.joinPipe.transform(
              items.map((item) =>
                this.datePipe.transform(item.expirationDate!, 'date-dot'),
              ),
              { maxLength: 5 },
            ),
          }),
        },
      );

    const groupedWarnings = groupBy(
      filteredDrivingStatuses().warnings,
      (item) => DateTime.fromISO(item.expirationDate!) < DateTime.now(),
    );
    const expiredWarnings = groupedWarnings['true'] ?? [];
    const expiringWarnings = groupedWarnings['false'] ?? [];

    handleDrivingLicenseExpiry(
      filteredDrivingStatuses().errors,
      'drivingLicenseExpired',
      'drivingLicenseExpired',
    );
    handleDrivingLicenseExpiry(
      expiredWarnings,
      'drivingLicenseExpired',
      'drivingLicenseExpired',
    );
    handleDrivingLicenseExpiry(
      expiringWarnings,
      'drivingLicenseExpires',
      'drivingLicenseExpires',
      true,
    );
  }

  public getExpiryStatuses<T>(
    items: T[],
    getDate: (item: T) => string | null | undefined,
    getColor: (date: string) => CellColor,
  ) {
    if (!items.length) return null;

    const sortedItems = orderBy(items, getDate)
      .map((item) => {
        const expirationDate = getDate(item);
        if (!expirationDate) return null;

        return { ...item, color: getColor(expirationDate) };
      })
      .filter((item) => item != null);

    return {
      errors: sortedItems.filter((item) => item.color === 'red'),
      warnings: sortedItems.filter((item) => item.color === 'yellow'),
    };
  }

  public groupOptions<T>(
    options: OnyxOption<T>[],
    fields?: {
      showDisabledSubheading: boolean;
    },
  ): OnyxOptionsGroup<T>[] {
    return chain(options)
      .groupBy((option) => option?.disabled ?? false)
      .map((groupOptions, disabled) => ({
        id: `${disabled === 'false' ? '0.valid' : '1.disabled'}`,
        ...(fields?.showDisabledSubheading &&
          disabled === 'true' && { subheading: 'labels.blocked' }),
        options: groupOptions,
        disabled: disabled === 'true',
      }))
      .filter(({ options }) => options.length > 0)
      .orderBy(({ disabled }) => disabled)
      .value();
  }

  public addExpiryIssue<
    T extends { color: CellColor; expirationDate?: OnyxOptional<string> },
  >(
    expiryList: T[],
    singleMessage: string,
    multipleMessage: string,
    issues: ValidationIssue[],
    options?: {
      forceSeverity?: ValidationSeverity;
      getData?: (item: T) => Record<string, unknown>;
      getListData?: (items: T[]) => Record<string, unknown>;
    },
  ): void {
    const count = expiryList.length;
    const { forceSeverity, getData, getListData } = options ?? {};

    if (count === 1) {
      issues.push({
        severity:
          forceSeverity ??
          (expiryList[0].color === 'red'
            ? ValidationSeverity.ERROR
            : ValidationSeverity.WARNING),
        message: singleMessage,
        data: getData ? getData(expiryList[0]) : {},
      });
    } else if (count > 1) {
      issues.push({
        severity:
          forceSeverity ??
          (expiryList.every((item) => item.color === 'red')
            ? ValidationSeverity.ERROR
            : ValidationSeverity.WARNING),
        message: multipleMessage,
        data: getListData ? getListData(expiryList) : { count },
      });
    }
  }

  public checkSetAllowedCountries(
    primary: TelematicsItem | Fleet,
    secondary: TelematicsItem | Fleet,
    issues: ValidationIssue[],
    context: ValidationContext,
  ): void {
    if (
      !intersection(
        primary.allowedCountries.whitelist,
        secondary.allowedCountries.whitelist,
      ).length
    ) {
      issues.push({
        severity: {
          [ValidationContext.SET]: ValidationSeverity.WARNING,
          [ValidationContext.DELEGATION]: ValidationSeverity.ERROR,
          [ValidationContext.DRIVER]: ValidationSeverity.WARNING,
          [ValidationContext.FLEET]: ValidationSeverity.WARNING,
          [ValidationContext.ORDER]: ValidationSeverity.WARNING,
          [ValidationContext.SWAP_PANEL]: ValidationSeverity.WARNING,
        }[context],
        message: `${this.I18N}.differentCountries`,
      });
    }
  }

  private hasVehicleTrailer(
    vehicle: OnyxOptional<Fleet | TelematicsItem>,
    trailer: OnyxOptional<Fleet | TelematicsItem | SimplifiedFleet>,
    context: ValidationContext,
  ) {
    const vehicleHasTrailer = !!(
      vehicle &&
      ('assignedTrailer' in vehicle ? vehicle.assignedTrailer : vehicle.trailer)
    );

    return (
      trailer ?? (context !== ValidationContext.DELEGATION && vehicleHasTrailer)
    );
  }

  private getRequiredCategoryMap(): Record<
    FleetCategory,
    { withTrailer: string[]; withoutTrailer: string[] }
  > {
    return {
      [FleetCategory.STRAIGHT_TRUCK]: {
        withTrailer: ['ce', 'c1e'],
        withoutTrailer: ['c', 'ce', 'c1', 'c1e'],
      },
      [FleetCategory.SEMI_TRUCK]: {
        withTrailer: ['ce'],
        withoutTrailer: ['c', 'ce'],
      },
      [FleetCategory.VAN]: {
        withTrailer: ['be'],
        withoutTrailer: ['b', 'be'],
      },
      [FleetCategory.TRAILER]: {
        withTrailer: ['be', 'ce', 'c1e'],
        withoutTrailer: ['be', 'ce', 'c1e'],
      },
      [FleetCategory.SEMI_TRAILER]: {
        withTrailer: ['ce'],
        withoutTrailer: ['ce'],
      },
    };
  }
}
